import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./LocationManagementPage.css";

const blankForm = {
  code: "",
  name: "",
  locationType: "SHOP",
  brand: "HANGTEN",
  groupCode: "",
  phone: "",
  username: "",
  password: "pos2025",
  sharedGroup: "",
  createPosUser: true,
  note: "",
};

function cleanCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");
}

function normalizeLocation(row) {
  return {
    code: row.code || "",
    name: row.name || row.shopName || row.code || "",
    locationType: row.location_type || row.locationType || row.type || "SHOP",
    brand: row.brand || "",
    groupCode: row.group_code || row.groupCode || "",
    isActive: row.is_active ?? row.isActive ?? true,
    note: row.note || "",
  };
}

export default function LocationManagementPage() {
  const [form, setForm] = useState(blankForm);
  const [locations, setLocations] = useState([]);
  const [filterType, setFilterType] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCode, setEditingCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadLocations() {
    setLoading(true);
    setError("");

    try {
      let response = await fetch(`${API_BASE}/api/erp/location-master`);

      if (!response.ok) {
        response = await fetch(`${API_BASE}/api/erp/locations?type=ALL&activeOnly=false`);
      }

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load locations");
      }

      const list = data.locations || data.rows || [];
      setLocations(list.map(normalizeLocation));
    } catch (err) {
      setError(err.message || "Failed to load locations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLocations();
  }, []);

  const filteredLocations = useMemo(() => {
    const q = search.trim().toUpperCase();

    return locations.filter((loc) => {
      const typeOk = filterType === "ALL" || loc.locationType === filterType;

      const searchOk =
        !q ||
        loc.code.toUpperCase().includes(q) ||
        loc.name.toUpperCase().includes(q) ||
        loc.brand.toUpperCase().includes(q) ||
        loc.groupCode.toUpperCase().includes(q);

      return typeOk && searchOk;
    });
  }, [locations, filterType, search]);

  function updateForm(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "code") {
        const code = cleanCode(value);
        next.code = code;

        if (!prev.username || prev.username === prev.code) {
          next.username = code;
        }
      }

      return next;
    });
  }

  function resetForm() {
    setEditingCode("");
    setForm(blankForm);
    setMessage("");
    setError("");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const code = cleanCode(form.code);
    const name = form.name.trim();

    if (!code) {
      setError("Location code is required.");
      return;
    }

    if (!name) {
      setError("Location name is required.");
      return;
    }

    if (form.createPosUser && !form.password.trim()) {
      setError("POS password is required.");
      return;
    }

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const payload = {
        code,
        name,
        locationType: form.locationType,
        brand: form.brand,
        groupCode: form.groupCode,
        phone: form.phone,
        username: cleanCode(form.username || code),
        password: form.password,
        sharedGroup: form.sharedGroup,
        createPosUser: form.createPosUser,
        note: form.note,
      };

      const url = editingCode
        ? `${API_BASE}/api/erp/locations/${encodeURIComponent(editingCode)}`
        : `${API_BASE}/api/erp/locations`;

      const response = await fetch(url, {
        method: editingCode ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to save location");
      }

      setMessage(editingCode ? `Updated ${code}. Firebase meta and POS user updated.` : `Saved ${code}. Firebase meta and POS user synced.`);
      setEditingCode("");
      setForm(blankForm);
      await loadLocations();
    } catch (err) {
      setError(err.message || "Failed to save location");
    } finally {
      setSaving(false);
    }
  }

  function startEditLocation(loc) {
    setEditingCode(loc.code);
    setMessage("");
    setError("");
    setForm({
      code: loc.code || "",
      name: loc.name || "",
      locationType: loc.locationType || "SHOP",
      brand: loc.brand || "HANGTEN",
      groupCode: loc.groupCode || "",
      phone: "",
      username: loc.code || "",
      password: "",
      sharedGroup: "",
      createPosUser: true,
      note: loc.note || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteLocation(loc) {
    const ok = window.confirm(
      "Deactivate " + loc.code + "?\\n\\nThis will NOT delete old sales/inventory data. It will only hide/inactivate the location."
    );

    if (!ok) return;

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/erp/locations/${encodeURIComponent(loc.code)}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to deactivate location");
      }

      setMessage(loc.code + " deactivated. Old data was not deleted.");
      await loadLocations();
    } catch (err) {
      setError(err.message || "Failed to deactivate location");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout title="Location Management">
      <div className="locationMasterPage">
        <div className="locationHero">
          <div>
            <span>OFFICE CONTROL</span>
            <h1>Location Management</h1>
            <p>
              Create shop or warehouse from ERP dashboard. Firebase shop meta and POS login can be created automatically.
            </p>
          </div>

          <button type="button" onClick={loadLocations} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {message && <div className="locationMsg locationMsg--ok">{message}</div>}
        {error && <div className="locationMsg locationMsg--error">{error}</div>}

        <form className="locationFormCard" onSubmit={handleSubmit}>
          <div className="locationFormHead">
            <span>CREATE LOCATION</span>
            <h2>{editingCode ? "Edit Shop / Warehouse" : "Shop / Warehouse Setup"}</h2>
          </div>

          <div className="locationFormGrid">
            <label>
              <span>Location Code</span>
              <input
                value={form.code}
                disabled={Boolean(editingCode)}
                onChange={(event) => updateForm("code", event.target.value)}
                placeholder="NPTHT"
              />
            </label>

            <label>
              <span>Location Name</span>
              <input
                value={form.name}
                onChange={(event) => updateForm("name", event.target.value)}
                placeholder="Naypyidaw Hangten"
              />
            </label>

            <label>
              <span>Type</span>
              <select
                value={form.locationType}
                onChange={(event) => updateForm("locationType", event.target.value)}
              >
                <option value="SHOP">SHOP</option>
                <option value="WAREHOUSE">WAREHOUSE</option>
              </select>
            </label>

            <label>
              <span>Brand</span>
              <select
                value={form.brand}
                onChange={(event) => updateForm("brand", event.target.value)}
              >
                <option value="HANGTEN">HANGTEN</option>
                <option value="PRETTYFIT">PRETTYFIT</option>
                <option value="MIXED">MIXED</option>
              </select>
            </label>

            <label>
              <span>Group Code</span>
              <input
                value={form.groupCode}
                onChange={(event) => updateForm("groupCode", event.target.value.toUpperCase())}
                placeholder="YGN / MDY / AYM"
              />
            </label>

            <label>
              <span>Phone</span>
              <input
                value={form.phone}
                onChange={(event) => updateForm("phone", event.target.value)}
                placeholder="09..."
              />
            </label>

            <label>
              <span>POS Username</span>
              <input
                value={form.username}
                onChange={(event) => updateForm("username", event.target.value.toUpperCase())}
                placeholder="Same as code"
              />
            </label>

            <label>
              <span>POS Password</span>
              <input
                value={form.password}
                onChange={(event) => updateForm("password", event.target.value)}
                placeholder="pos2025"
              />
            </label>

            <label className="locationFormWide">
              <span>Shared Group</span>
              <input
                value={form.sharedGroup}
                onChange={(event) => updateForm("sharedGroup", event.target.value)}
                placeholder="Example: OSPF,OSHT"
              />
            </label>

            <label className="locationFormWide">
              <span>Note</span>
              <input
                value={form.note}
                onChange={(event) => updateForm("note", event.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>

          <div className="locationFormBottom">
            <label className="locationCheck">
              <input
                type="checkbox"
                checked={form.createPosUser}
                onChange={(event) => updateForm("createPosUser", event.target.checked)}
              />
              <span>Create POS username/password in Firebase users</span>
            </label>

            <div className="locationActions">
              <button type="button" className="locationBtnGhost" onClick={resetForm}>
                Clear
              </button>
              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : editingCode ? "Update Location" : "Save Location"}
              </button>
            </div>
          </div>
        </form>

        <div className="locationListCard">
          <div className="locationListHead">
            <div>
              <span>LOCATION MASTER</span>
              <h2>Shops & Warehouses</h2>
            </div>

            <div className="locationListTools">
              <select value={filterType} onChange={(event) => setFilterType(event.target.value)}>
                <option value="ALL">All</option>
                <option value="SHOP">Shop</option>
                <option value="WAREHOUSE">Warehouse</option>
              </select>

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search code, name, brand..."
              />
            </div>
          </div>

          <div className="locationTableWrap">
            <table className="locationTable">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Brand</th>
                  <th>Group</th>
                  <th>Status</th>
                  <th>Note</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((loc) => (
                  <tr key={loc.code}>
                    <td><b>{loc.code}</b></td>
                    <td>{loc.name}</td>
                    <td>
                      <span className={`locationPill ${loc.locationType === "WAREHOUSE" ? "isWarehouse" : "isShop"}`}>
                        {loc.locationType}
                      </span>
                    </td>
                    <td>{loc.brand || "-"}</td>
                    <td>{loc.groupCode || "-"}</td>
                    <td>{loc.isActive ? "Active" : "Inactive"}</td>
                    <td>{loc.note || "-"}</td>
                    <td>
                      <div className="locationRowActions">
                        <button type="button" onClick={() => startEditLocation(loc)}>
                          Edit
                        </button>
                        <button type="button" className="danger" onClick={() => deleteLocation(loc)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredLocations.length === 0 && (
                  <tr>
                    <td colSpan="8" className="locationEmpty">
                      No locations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
